import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { userLoggedIn, userLoggedOut } from "../authSlice";

const USER_API = "https://fetc.in/api/v1/user";

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: fetchBaseQuery({
    baseUrl: USER_API,
    credentials: "include", // Include cookies for session-based auth
  }),
  tagTypes: ["User"],
  endpoints: (builder) => ({

    // REGISTER: Creates a new user account
    registerUser: builder.mutation({
      query: (inputData) => ({
        url: "/register",
        method: "POST",
        body: inputData,
        headers: { "Content-Type": "application/json" },
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.user) dispatch(userLoggedIn({ user: data.user }));
        } catch (error) {
          console.error("Registration failed:", {
            status: error?.status,
            data: error?.data,
            message: error?.data?.message || "Unknown error",
          });
        }
      },
    }),

    // LOGIN: Authenticates a user
    loginUser: builder.mutation({
      query: (inputData) => ({
        url: "/login",
        method: "POST",
        body: inputData,
        headers: { "Content-Type": "application/json" },
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.user) dispatch(userLoggedIn({ user: data.user }));
        } catch (error) {
          console.error("Login failed:", {
            status: error?.status,
            data: error?.data,
            message: error?.data?.message || "Unknown error",
          });
        }
      },
    }),

    // LOGOUT: Ends the user session
    logoutUser: builder.mutation({
      query: () => ({
        url: "/logout",
        method: "POST",
        headers: { "Content-Type": "application/json" },
      }),
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          dispatch(userLoggedOut());
        } catch (error) {
          console.error("Logout failed:", {
            status: error?.status,
            data: error?.data,
            message: error?.data?.message || "Unknown error",
          });
        }
      },
    }),

    // LOAD USER: Fetches the current user's profile
    loadUser: builder.query({
      query: () => ({
        url: "/profile",
        method: "GET",
      }),
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.user) {
            dispatch(userLoggedIn({ user: data.user }));
          } else {
            dispatch(userLoggedOut());
          }
        } catch (error) {
          console.error("Load user failed:", {
            status: error?.status,
            data: error?.data,
            message: error?.data?.message || error?.error || "Unknown error",
          });
          dispatch(userLoggedOut());
        }
      },
    }),

    // UPDATE PROFILE
    updateUser: builder.mutation({
      query: (formData) => ({
        url: "/profile/update",
        method: "PUT",
        body: formData,
      }),
      invalidatesTags: ["User"],
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.user) dispatch(userLoggedIn({ user: data.user }));
        } catch (error) {
          console.error("Update user failed:", {
            status: error?.status,
            data: error?.data,
            message: error?.data?.message || "Unknown error",
          });
        }
      },
    }),

    // ADMIN: Get all users
    getAllUsers: builder.query({
      query: () => "/admin/users",
      providesTags: ["User"],
    }),

    // ADMIN: Get user by ID
    getUserById: builder.query({
      query: (id) => `/admin/${id}`,
      providesTags: (result, error, id) => [{ type: "User", id }],
    }),

    // ADMIN: Edit user by ID
  editUserById: builder.mutation({
  query: ({ id, data = {} }) => {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        formData.append(key, value);
      }
    });

    return {
      url: `/admin/${id}`,
      method: "PUT",
      body: formData,
    };
  },
  invalidatesTags: (result, error, { id }) => [
    { type: "User", id },
    "User",
  ],
}),


    // ADMIN: Create a new user
    createUser: builder.mutation({
      query: (data) => ({
        url: "/create",
        method: "POST",
        body: data,
        headers: { "Content-Type": "application/json" },
      }),
      invalidatesTags: ["User"],
    }),

    // ADMIN: Delete user by ID
    deleteUserById: builder.mutation({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [{ type: "User", id }, "User"],
    }),

    // FORGOT PASSWORD
    forgotPassword: builder.mutation({
      query: (data) => ({
        url: "/forgot-password",
        method: "POST",
        body: data,
        headers: { "Content-Type": "application/json" },
      }),
    }),

    // RESET PASSWORD
    resetPassword: builder.mutation({
      query: ({ token, password }) => ({
        url: `/reset-password/${token}`,
        method: "POST",
        body: { password },
        headers: { "Content-Type": "application/json" },
      }),
    }),

    resendVerification: builder.mutation({
  query: (data) => ({
    url: "/resend-verification",
    method: "POST",
    body: data,
  }),
}),

    verifyEmail: builder.mutation({
  query: ({ token }) => ({
    url: `/verify-email/${token}`,
    method: "GET",
    headers: { "Content-Type": "application/json" },
  }),
}),


  }),
});

export const {
  useRegisterUserMutation,
  useLoginUserMutation,
  useLogoutUserMutation,
  useLoadUserQuery,
  useUpdateUserMutation,
  useGetAllUsersQuery,
  useGetUserByIdQuery,
  useEditUserByIdMutation,
  useCreateUserMutation,
  useDeleteUserByIdMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useVerifyEmailMutation,
  useResendVerificationMutation,
} = authApi;
